import { BadgeCheck, Clock, ShieldOff } from 'lucide-react';
import { useParams } from 'react-router-dom';

import type { PublicBadge } from '@repo/contracts';
import { AUDIT_STANDARD_LABELS } from '@repo/contracts/services';
import { BADGE_VALIDITY_MONTHS } from '@repo/contracts/services-pricing';

import { ApiError } from '../api/client';
import { useBadge } from '../api/services';
import {
  ButtonLink,
  Card,
  Container,
  Eyebrow,
  formatDate,
} from '../components/ui';

const STATE_COPY: Record<
  PublicBadge['state'],
  { icon: typeof BadgeCheck; fill: string; title: string; body: string }
> = {
  expired: {
    body: `This badge was valid, but its audit is more than ${BADGE_VALIDITY_MONTHS} months old. The site may have changed since.`,
    fill: 'bg-tag-major',
    icon: Clock,
    title: 'Expired badge',
  },
  revoked: {
    body: 'Floto has withdrawn this badge. It should no longer be displayed.',
    fill: 'bg-tag-critical',
    icon: ShieldOff,
    title: 'Revoked badge',
  },
  valid: {
    body: 'This site was audited and verified by Floto’s accessibility team. The details below are held by Floto and can’t be edited by the site owner.',
    fill: 'bg-tag-success',
    icon: BadgeCheck,
    title: 'Valid Floto badge',
  },
};

export function VerifyPage() {
  const { badgeId = '' } = useParams();
  const query = useBadge(badgeId);

  if (query.isPending) {
    return (
      <Container className="py-28">
        <p aria-live="polite" className="text-body text-text-secondary">
          Checking badge…
        </p>
      </Container>
    );
  }

  if (query.isError) {
    const notFound =
      query.error instanceof ApiError && query.error.status === 404;
    return (
      <Container className="max-w-3xl py-28">
        <Eyebrow>Badge verification</Eyebrow>
        <h1 className="mt-3 font-report text-report-section font-medium text-text-primary">
          {notFound
            ? 'This is not a valid Floto badge'
            : 'We couldn’t check this badge'}
        </h1>
        <p className="mt-3 text-report-lede text-text-secondary">
          {notFound
            ? 'No Floto audit matches this badge. If you found it on a website, the site may be displaying it without having been audited by Floto.'
            : 'Please try again in a moment.'}
        </p>
      </Container>
    );
  }

  const badge = query.data;
  const state = STATE_COPY[badge.state];
  const Icon = state.icon;

  return (
    <Container className="max-w-3xl py-14 md:py-20">
      <Eyebrow>Badge verification · {badge.reference}</Eyebrow>
      <div
        className={`mt-4 flex gap-4 rounded-card p-6 ${state.fill}`}
        role="status"
      >
        <Icon
          aria-hidden
          className="mt-1 shrink-0 text-text-primary"
          size={28}
          strokeWidth={1.5}
        />
        <div>
          <h1 className="font-report text-report-section font-medium text-text-primary">
            {state.title}
          </h1>
          <p className="mt-1 text-body text-text-primary">{state.body}</p>
        </div>
      </div>

      <Card className="mt-6">
        <h2 className="sr-only">Audit details</h2>
        <dl className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
          {[
            ['Site', badge.domain],
            ['Organisation', badge.company],
            ['Standard', AUDIT_STANDARD_LABELS[badge.standard]],
            ['Pages audited', String(badge.pagesAudited)],
            ['Audited', formatDate(badge.auditedAt)],
            [
              badge.state === 'expired' ? 'Expired' : 'Valid until',
              formatDate(badge.expiresAt),
            ],
          ].map(([label, value]) => (
            <div key={label}>
              <dt className="text-ui text-text-secondary">{label}</dt>
              <dd className="mt-0.5 break-words text-ui-lg font-semibold text-text-primary">
                {value}
              </dd>
            </div>
          ))}
        </dl>
        <p className="mt-6 border-t border-hairline pt-5 text-ui text-text-secondary">
          Check that the site you saw this badge on is{' '}
          <strong className="text-text-primary">{badge.domain}</strong>. A badge
          shown on any other site does not apply to it.
        </p>
      </Card>

      <section aria-labelledby="means-title" className="mt-10">
        <h2 className="text-card-title text-text-primary" id="means-title">
          What this badge means
        </h2>
        <ul className="mt-4 list-disc space-y-2 pl-5 text-body text-text-secondary">
          <li>
            Floto&rsquo;s accessibility team audited and verified the pages in
            scope against {AUDIT_STANDARD_LABELS[badge.standard]} on{' '}
            {formatDate(badge.auditedAt)} and delivered a full report to the
            site owner.
          </li>
          <li>
            It is not a legal certification of compliance, and it doesn&rsquo;t
            mean the site had no issues — it records that an expert audit took
            place, when, and against which standard.
          </li>
          <li>
            Sites change. Badges expire {BADGE_VALIDITY_MONTHS} months after the
            audit.
          </li>
        </ul>
        <ButtonLink className="mt-8" to="/" variant="secondary">
          About Floto accessibility audits
        </ButtonLink>
      </section>
    </Container>
  );
}
